import { Box, Button, Typography } from "@mui/material";
import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { menLinksSmall, womenLinksSmall } from "../../constants/avatarLinks";
import { CitySimple, CONFIRMATION_DELAY } from "../../constants/const";
import { BOOZE_LIMITS } from "../../constants/game";
import { Messages } from "../../constants/messages";
import { RankInfos } from "../../constants/ranking";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import { goToMapSlotPage } from "../../helper/map";
import useCurrentBlock from "../../hook/useCurrentBlock";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import { claimBoozeProfitAction } from "../../reducers/game.slice";
import {
  convertDateTimeSimple,
  toastSuccess,
  toUSDFormat,
} from "../../utils/utils";

import useStyles from "./index.styles";

const BoozeInfoPanelList = () => {
  const { classes } = useStyles();
  const rankInfosRef = useRef<HTMLDivElement>(null);
  const currentBlock = useCurrentBlock();
  const { account } = useWallet();
  const currentTime = useCurrentTime();
  const dispatch = useAppDispatch();
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  const { myProfile } = useAppSelector((state) => state.profile);
  const {
    smugglingInfo,
    businessProfile,
    smugglingMarketLaunchBlock,
    smugglingMarketUpdateInterval,
    boozeTotalProfit,
  } = useAppSelector((state) => state.game);

  const isMyBusiness =
    account?.toLowerCase() === businessProfile?.address?.toLowerCase();

  const handleClaimProfit = () => {
    dispatchTxAction(
      dispatch,
      claimBoozeProfitAction({ account, cityId: myProfile.city || 0 }),
      () => {
        toastSuccess(Messages.BOOZE.CLAIM_PROFIT.SUCCESS);
        updateBalance(account, signMsg, signature);
      },
      CONFIRMATION_DELAY
    );
  };

  useEffect(() => {
    if (myProfile.rankNumber !== undefined && rankInfosRef.current) {
      const rowHeight = 32; // Approximate height of each row
      const scrollPosition = rowHeight * Math.max(0, myProfile.rankNumber - 3);
      rankInfosRef.current.scrollTop = scrollPosition;
    }
  }, [myProfile.rankNumber]);

  return (
    <Box className={classes.container}>
      {/* Rank unit allowance panel */}
      <Box className={classes.panel}>
        <Typography className={classes.title}>Rank unit allowance</Typography>

        <Box ref={rankInfosRef} className={classes.rankInfos}>
          {RankInfos.map((rank, index) => {
            return (
              <Box
                key={index}
                className={classes.infoRow}
                sx={{
                  backgroundColor:
                    myProfile.rankNumber === index + 1
                      ? "rgba(255, 255, 255, 0.1)"
                      : "transparent",
                }}
              >
                <Typography className={classes.infoText}>
                  {rank.rank}:
                </Typography>

                <Typography className={classes.infoValue}>
                  {BOOZE_LIMITS[index]}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Information panel */}
      <Box className={classes.panel}>
        <Typography className={classes.title}>Information</Typography>
        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>Next price:</Typography>
          <Typography className={classes.infoValue}>
            in{" "}
            {smugglingMarketUpdateInterval -
              ((currentBlock - smugglingMarketLaunchBlock) %
                smugglingMarketUpdateInterval)}{" "}
            Blocks
          </Typography>
        </Box>
        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>
            Total buys 7 days:
          </Typography>
          <Typography className={classes.infoValue}>
            {toUSDFormat(smugglingInfo.totalBuyAmount)}
          </Typography>
        </Box>
        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>
            Total sells 7 days:
          </Typography>
          <Typography className={classes.infoValue}>
            {toUSDFormat(smugglingInfo.totalSellAmount)}
          </Typography>
        </Box>
        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>Profits 7 days:</Typography>
          <Typography
            className={`${classes.infoValue} ${
              smugglingInfo.totalOwnerProfit > 0
                ? classes.greenText
                : smugglingInfo.totalOwnerProfit < 0
                ? classes.redText
                : ""
            }`}
          >
            {smugglingInfo.totalOwnerProfit > 0 ? "+" : ""}
            {toUSDFormat(smugglingInfo.totalOwnerProfit)}
          </Typography>
        </Box>
        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>Total profits:</Typography>
          <Typography
            className={`${classes.infoValue} ${
              boozeTotalProfit > 0
                ? classes.greenText
                : boozeTotalProfit < 0
                ? classes.redText
                : ""
            }`}
          >
            {boozeTotalProfit > 0 ? "+" : ""}
            {toUSDFormat(boozeTotalProfit)}
          </Typography>
        </Box>
      </Box>

      {/* Booze warehouse panel */}
      <Box className={classes.panel}>
        <Typography className={classes.title}>Booze warehouse</Typography>
        <Box className={classes.details}>
          {businessProfile?.ownerImageId ? (
            <Box
              component="img"
              className={classes.image}
              src={
                businessProfile?.ownerGender === 1
                  ? womenLinksSmall[
                      !businessProfile.ownerImageId
                        ? -1
                        : businessProfile.ownerImageId
                    ]
                  : menLinksSmall[
                      !businessProfile?.ownerImageId
                        ? -1
                        : businessProfile.ownerImageId
                    ]
              }
            ></Box>
          ) : (
            <Box sx={{ minWidth: 100 }}></Box>
          )}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: isMyBusiness ? 1 : 2,
            }}
          >
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Owner:</Typography>
              <Typography
                className={`${classes.infoValue} ${classes.ownerLink}`}
              >
                {businessProfile?.ownerName}
              </Typography>
            </Box>

            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Family:</Typography>
              <Typography className={classes.infoValue}>
                {businessProfile?.familyInfo?.name || " "}
              </Typography>
            </Box>

            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Location:</Typography>
              <Typography
                className={`${classes.infoValue} ${classes.ownerLink}`}
                sx={{ cursor: "pointer", color: "#1c7995" }}
                onClick={() =>
                  businessProfile &&
                  businessProfile.map &&
                  goToMapSlotPage(
                    businessProfile.cityId,
                    businessProfile.map?.slotX,
                    businessProfile.map?.slotY
                  )
                }
              >
                {businessProfile &&
                  businessProfile?.cityId >= 0 &&
                  `#${CitySimple[businessProfile.cityId]}-${
                    businessProfile.map?.slotX
                  }-${businessProfile.map?.slotY}`}
              </Typography>
            </Box>

            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Time owned:</Typography>
              <Typography className={classes.infoValue}>
                {convertDateTimeSimple(
                  currentTime -
                    (businessProfile?.timestamp || currentTime - 10000000)
                )}
              </Typography>
            </Box>

            {isMyBusiness && (
              <Button
                className={classes.claimButton}
                onClick={handleClaimProfit}
              >
                Claim profit
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BoozeInfoPanelList;
